import * as React from 'react';

import { cn } from './lib/cn';

/*
 * Data-table primitives backed by design-system tokens (§5 batch-5 probe).
 * Row height 36px, header 40px, hover/selected per §2.1.
 * Horizontal row separators only (no vertical column lines); pass `flush` when
 * the table sits edge-to-edge inside a shell (no outer border / radius — the
 * shell provides the frame).
 */

interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  flush?: boolean;
  /**
   * Scroll the frame horizontally instead of clipping what does not fit
   * (IMM-636). The default wrapper carries `overflow-hidden` — it is what gives
   * the non-`flush` variant its rounded corners — so a table with a `min-width`
   * wider than its container is silently thrown away rather than reachable.
   * With `scrollX` the same wrapper scrolls; the radius still clips, so the
   * frame is unchanged for any table that fits.
   */
  scrollX?: boolean;
  /**
   * Classes for the wrapper element. Its reason to exist is the height bound a
   * scrolling table needs: a scroll container paints its horizontal scrollbar
   * at its OWN bottom edge, so an unbounded one puts the bar below the fold and
   * the columns to the right are unreachable (BL-051 / IMM-617).
   */
  containerClassName?: string;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, flush = false, scrollX = false, containerClassName, ...props }, ref) => (
    <div
      className={cn(
        'bg-surface',
        flush ? 'border-b border-border' : 'rounded-lg border border-border',
        // Never both: `overflow-hidden` and `overflow-x-auto` sit in different
        // tailwind-merge groups, so leaving both on the element would leave the
        // winner to stylesheet order rather than to this decision.
        scrollX ? 'overflow-auto' : flush ? undefined : 'overflow-hidden',
        containerClassName,
      )}
    >
      <table ref={ref} className={cn('w-full border-collapse text-left text-base font-sans', className)} {...props} />
    </div>
  ),
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn('bg-neutral-50 text-text-tertiary', className)} {...props} />
  ),
);
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <tbody ref={ref} className={cn('', className)} {...props} />,
);
TableBody.displayName = 'TableBody';

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
  interactive?: boolean;
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, selected = false, interactive = false, ...props }, ref) => (
    <tr
      ref={ref}
      data-selected={selected || undefined}
      className={cn(
        'h-table-row align-middle transition-colors duration-micro ease-out',
        interactive && 'cursor-pointer hover:bg-hover-bg',
        selected && 'bg-selected-bg shadow-[inset_2px_0_0_0_var(--color-interactive)]',
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      scope="col"
      className={cn(
        'h-table-header border-b border-border px-4 text-base-sm font-medium text-text-tertiary',
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn('border-b border-border px-4 py-2 text-base-sm text-text-primary', className)}
      {...props}
    />
  ),
);
TableCell.displayName = 'TableCell';

export { Table, TableHeader, TableBody, TableRow, TableHead, TableCell };
